import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Toggle } from '../components/ui/toggle';
import { ArrowLeft, User, Eye, EyeOff, Bell, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BuilderSettings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState({ enabled: false, bio: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/builder-profile/me`, { headers: { Authorization: `Bearer ${token}` } });
        setProfile(res.data || { enabled: false, bio: '' });
      } catch { /* empty */ }
      setLoading(false);
    };
    fetch();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/builder-profile/toggle`, { enabled: profile.enabled, bio: profile.bio }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Settings saved');
    } catch (err) { toast.error('Failed to save'); }
    setSaving(false);
  };

  if (loading) return <div className="bg-amber-50 flex items-center justify-center text-stone-400">Loading...</div>;

  return (
    <div className="p-4">
      <div className="max-w-7xl pb-24">
        <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2 mb-6"><User className="h-6 w-6 text-amber-600" /> Builder Profile Settings</h1>

        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6 space-y-6">
          {/* Profile Visibility */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-stone-800 font-semibold text-base">Public Builder Profile</Label>
              <p className="text-xs text-stone-500 mt-0.5">When enabled, other logged-in beekeepers can see your profile, ideas, and builds</p>
            </div>
            <Toggle pressed={profile.enabled} onPressedChange={(v) => setProfile(prev => ({ ...prev, enabled: v }))}
              className="data-[state=on]:bg-green-600 data-[state=on]:text-white h-10 px-4" data-testid="profile-toggle">
              {profile.enabled ? <><Eye className="h-4 w-4 mr-1" /> Visible</> : <><EyeOff className="h-4 w-4 mr-1" /> Hidden</>}
            </Toggle>
          </div>

          {/* Display info */}
          <div>
            <Label className="text-stone-700 font-medium">Display Name</Label>
            <p className="text-sm text-stone-600 mt-1 bg-stone-50 rounded-lg px-3 py-2 border border-stone-200">{user?.name || 'Not set'}</p>
            <p className="text-[11px] text-stone-400 mt-1">This comes from your account settings</p>
          </div>

          <div>
            <Label className="text-stone-700 font-medium">Company/Apiary Name</Label>
            <p className="text-sm text-stone-600 mt-1 bg-stone-50 rounded-lg px-3 py-2 border border-stone-200">{user?.company_name || 'Not set'}</p>
          </div>

          {/* Bio */}
          <div>
            <Label className="text-stone-700 font-medium">Bio</Label>
            <Textarea value={profile.bio || ''} onChange={e => setProfile(prev => ({ ...prev, bio: e.target.value }))}
              placeholder="Tell the community about yourself and your beekeeping journey..." className="mt-1 min-h-[100px]" maxLength={500} />
            <p className="text-[11px] text-stone-400 mt-1">{(profile.bio || '').length}/500 characters</p>
          </div>

          {/* Privacy notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-amber-800 mb-1">Privacy</h3>
            <ul className="text-xs text-amber-700 space-y-1">
              <li>Your profile is only visible to other logged-in Thrive beekeepers</li>
              <li>Your email address is never shown publicly</li>
              <li>You can disable your profile at any time — it will be hidden immediately</li>
              <li>Only your shared ideas and Made It posts are shown — no private hive data</li>
            </ul>
          </div>

          {/* Social Sharing Opt-in */}
          <div className="pt-4 border-t border-stone-200">
            <h3 className="text-sm font-semibold text-stone-800 mb-2">Social Media</h3>
            <p className="text-xs text-stone-500 mb-3">If you win a challenge, Thrive may feature your build on our social media pages. You can opt out at any time on individual ideas.</p>
          </div>

          <Button onClick={save} disabled={saving} className="w-full h-11 bg-amber-600 hover:bg-amber-700 text-white rounded-full font-semibold">
            <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>

        {/* View Profile Link */}
        {profile.enabled && user?.id && (
          <div className="text-center mt-4">
            <button onClick={() => navigate(`/builder/${user.id}`)} className="text-sm text-amber-600 font-medium hover:underline">
              View my public profile →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BuilderSettings;
